import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { Panel, PageHead, Verdict, Stat } from "@/components/kit";
import { WeeklyReport } from "@/components/WeeklyReport";

const vColor = { green: "#39FF14", yellow: "#FFD700", red: "#FF003C", pending: "#00FFFF" };

export default function Analytics() {
    const { data: metrics = [] } = useQuery({
        queryKey: ["metrics"],
        queryFn: async () => (await api.get("/metrics")).data,
    });
    const { data: compare } = useQuery({
        queryKey: ["compare"],
        queryFn: async () => (await api.get("/metrics/compare")).data,
    });
    const { data: byLang } = useQuery({
        queryKey: ["by-language"],
        queryFn: async () => (await api.get("/reports/by-language")).data,
    });
    const langs = byLang?.languages || [];

    const totals = compare?.totals || {};
    const byChannel = compare?.by_channel || [];
    const byFormat = compare?.by_format || [];

    return (
        <div data-testid="analytics-page">
            <PageHead
                title="ANALIZADOR DE RENDIMIENTO"
                sub="Métricas reales (YouTube Analytics) → dictamen verde/amarillo/rojo por canal, nicho y formato. Feedback loop hacia el recomendador."
            />

            <div className="mb-6 grid grid-cols-2 gap-px bg-border lg:grid-cols-4">
                <Stat label="Vistas totales" value={(totals.views || 0).toLocaleString()} accent />
                <Stat label="Videos medidos" value={totals.videos || 0} />
                <Stat
                    label="Canales óptimos"
                    value={byChannel.filter((c) => c.verdict === "green").length}
                />
                <Stat label="A revisar" value={metrics.filter((m) => m.verdict !== "green").length} />
            </div>

            <WeeklyReport />

            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Panel title="Vistas por canal" testId="chart-channel">
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={byChannel}>
                            <CartesianGrid stroke="#1a1a1a" vertical={false} />
                            <XAxis dataKey="name" tick={{ fill: "#888", fontSize: 10 }} />
                            <YAxis tick={{ fill: "#888", fontSize: 10 }} />
                            <Tooltip
                                contentStyle={{ background: "#050505", border: "1px solid #262626", borderRadius: 0 }}
                                cursor={{ fill: "rgba(0,255,255,0.05)" }}
                            />
                            <Bar dataKey="views">
                                {byChannel.map((c, i) => (
                                    <Cell key={i} fill={vColor[c.verdict]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Panel>

                <Panel title="Comparativa por formato" testId="chart-format">
                    <div className="space-y-4">
                        {byFormat.map((f) => (
                            <div key={f.format} data-testid={`format-${f.format}`}>
                                <div className="mb-1 flex items-center justify-between text-sm">
                                    <span className="uppercase tracking-wider">{f.format}</span>
                                    <Verdict v={f.verdict} />
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                                    <span>{f.views.toLocaleString()} vistas</span>
                                    <span>CTR {f.avg_ctr != null ? `${f.avg_ctr}%` : "—"}</span>
                                    <span>Ret {f.avg_retention != null ? `${f.avg_retention}%` : "procesando…"}</span>
                                </div>
                            </div>
                        ))}
                        {!byFormat.length && (
                            <p className="text-sm text-muted-foreground">Sin datos aún.</p>
                        )}
                    </div>
                </Panel>
            </div>

            {langs.length > 0 && (
                <Panel title="Rendimiento por idioma" testId="by-language-panel" className="mb-6">
                    <p className="mb-4 text-xs text-muted-foreground">
                        {byLang?.best
                            ? <>El idioma que mejor rinde ahora es <b className="text-primary">{byLang.best}</b> (por vistas). Escala tu doblaje ahí.</>
                            : "Cuando subas versiones dobladas verás aquí qué idioma escalar."}
                    </p>
                    <div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
                        {langs.map((l, i) => (
                            <div key={l.lang} className="bg-card p-4" data-testid={`lang-${l.lang}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-head text-sm uppercase tracking-wider">
                                        {l.label}
                                    </span>
                                    {i === 0 && (
                                        <span className="border border-primary px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-primary">
                                            Líder
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 font-head text-2xl text-primary">
                                    {l.views.toLocaleString("es")}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">vistas totales</div>
                                <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                                    <span>{l.videos} videos</span>
                                    <span>Ret {l.avg_retention != null ? `${l.avg_retention}%` : "—"}</span>
                                    <span>+{l.subs} subs</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-3 text-[10px] text-muted-foreground">{byLang?.note}</p>
                </Panel>
            )}

            <Panel title="Dictamen por video" testId="metrics-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="label border-b border-border">
                                <th className="py-2 pr-4">Título</th>
                                <th className="py-2 pr-4">Canal</th>
                                <th className="py-2 pr-4">Vistas</th>
                                <th className="py-2 pr-4">CTR</th>
                                <th className="py-2 pr-4">Retención</th>
                                <th className="py-2 pr-4">Subs</th>
                                <th className="py-2">Dictamen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.map((m) => (
                                <tr
                                    key={m.id}
                                    className="border-b border-border/50 transition-colors hover:bg-accent"
                                    data-testid={`metric-row-${m.id}`}
                                >
                                    <td className="py-3 pr-4">
                                        {m.title}
                                        {m.source === "youtube" && (
                                            <span
                                                className="ml-2 border px-1 py-0.5 text-[9px] uppercase tracking-wider"
                                                style={{ color: "#FF003C", borderColor: "#FF003C" }}
                                            >
                                                YT real
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-3 pr-4 text-xs text-primary">{m.channel_name}</td>
                                    <td className="py-3 pr-4">{m.views.toLocaleString()}</td>
                                    <td className="py-3 pr-4">{m.ctr != null ? `${m.ctr}%` : "—"}</td>
                                    <td className="py-3 pr-4">
                                        {m.avg_retention != null ? `${m.avg_retention}%` : (
                                            <span className="text-[10px] uppercase text-muted-foreground">procesando…</span>
                                        )}
                                    </td>
                                    <td className="py-3 pr-4 text-status-green">
                                        {m.subs_gained != null ? `+${m.subs_gained}` : "—"}
                                    </td>
                                    <td className="py-3">
                                        <Verdict v={m.verdict} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!metrics.length && (
                        <p className="py-6 text-sm text-muted-foreground">
                            Sin métricas. Aprueba y publica videos para alimentar el analizador.
                        </p>
                    )}
                </div>
            </Panel>
        </div>
    );
}
